import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type PrometheusTimeSeries = {
  metric: Record<string, string>;
  values: [number, string][];
};

export type PrometheusData = {
  resultType: string;
  result: PrometheusTimeSeries[];
};

export type CustomMetricsState = {
  metricNames: string[];
  namesLoading: boolean;
  namesError: string | null;
  timeseries: PrometheusData | null;
  timeseriesLoading: boolean;
  timeseriesError: string | null;
  tags: Record<string, string[]>;
  tagsLoading: boolean;
};

export type GetMetricNamesParams = {
  agentId: string;
};

export type GetTimeseriesParams = {
  metricName: string;
  startDate: string;
  endDate: string;
  step?: string;
  agentId?: string;
  environmentId?: string;
  tags?: Record<string, string>;
};

export type GetTagsParams = {
  metricName?: string;
  agentId?: string;
};

export const getMetricNames = createAsyncThunk("customMetrics/getNames", async (params: GetMetricNamesParams) => {
  const queryParams = new URLSearchParams();
  if (params.agentId) queryParams.append("agentId", params.agentId);
  const { data } = await makeRequest<{ data: string[] }>(`/v1/custom-metrics/names?${queryParams}`);
  return data;
});

export const getTimeseries = createAsyncThunk("customMetrics/getTimeseries", async (params: GetTimeseriesParams) => {
  const queryParams = new URLSearchParams({
    metricName: params.metricName,
    startDate: params.startDate,
    endDate: params.endDate,
  });
  if (params.step) queryParams.append("step", params.step);
  if (params.agentId) queryParams.append("agentId", params.agentId);
  if (params.environmentId) queryParams.append("environmentId", params.environmentId);
  if (params.tags) queryParams.append("tags", JSON.stringify(params.tags));

  const { data } = await makeRequest<{ data: PrometheusData }>(`/v1/custom-metrics/timeseries?${queryParams}`);
  return data;
});

export const getMetricTags = createAsyncThunk("customMetrics/getTags", async (params: GetTagsParams) => {
  const queryParams = new URLSearchParams();
  if (params.metricName) queryParams.append("metricName", params.metricName);
  if (params.agentId) queryParams.append("agentId", params.agentId);
  const { data } = await makeRequest<{ data: Record<string, string[]> }>(`/v1/custom-metrics/tags?${queryParams}`);
  return data;
});

const initialState: CustomMetricsState = {
  metricNames: [],
  namesLoading: false,
  namesError: null,
  timeseries: null,
  timeseriesLoading: false,
  timeseriesError: null,
  tags: {},
  tagsLoading: false,
};

const customMetricsSlice = createSlice({
  name: "customMetrics",
  initialState,
  reducers: {
    clearTimeseries: (state) => {
      state.timeseries = null;
      state.timeseriesError = null;
    },
    clearAll: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getMetricNames.pending, (state) => {
      state.namesLoading = true;
      state.namesError = null;
    });
    builder.addCase(getMetricNames.fulfilled, (state, action) => {
      state.namesLoading = false;
      state.metricNames = action.payload;
    });
    builder.addCase(getMetricNames.rejected, (state, action) => {
      state.namesLoading = false;
      state.namesError = action.error.message || "Failed to fetch metric names";
    });

    builder.addCase(getTimeseries.pending, (state) => {
      state.timeseriesLoading = true;
      state.timeseriesError = null;
    });
    builder.addCase(getTimeseries.fulfilled, (state, action) => {
      state.timeseriesLoading = false;
      state.timeseries = action.payload;
    });
    builder.addCase(getTimeseries.rejected, (state, action) => {
      state.timeseriesLoading = false;
      state.timeseriesError = action.error.message || "Failed to fetch timeseries data";
    });

    builder.addCase(getMetricTags.pending, (state) => {
      state.tagsLoading = true;
    });
    builder.addCase(getMetricTags.fulfilled, (state, action) => {
      state.tagsLoading = false;
      state.tags = action.payload;
    });
    builder.addCase(getMetricTags.rejected, (state) => {
      state.tagsLoading = false;
    });
  },
});

export const { clearTimeseries, clearAll } = customMetricsSlice.actions;
export const customMetricsReducer = customMetricsSlice.reducer;
